export class TocOptions {
  selector = "h2, h3, h4";
  scope = "#content-main";
  overwrite = false;
  prefix = "toc";
  listType = "ol";
  includeClasses = false;
}

function getHeaders(selector: string, scope: string): HTMLElement[] {
  let headers: HTMLElement[] = [];

  const target = document.querySelectorAll(scope);

  Array.prototype.forEach.call(target, function (elem: HTMLElement) {
    const elements = elem.querySelectorAll(selector);
    headers = headers.concat(Array.prototype.slice.call(elements));
  });

  return headers;
}

function getLevel(header: string): number {
  if (typeof header !== "string") {
    return 0;
  }

  const decs = header.match(/\d/g);

  if (decs) {
    const hNums = decs?.map(Number);
    return Math.min.apply(null, hNums);
  }

  return 1;
}

function createList(wrapper: HTMLElement, count: number, options: TocOptions): HTMLElement {
  while (count--) {
    const list = document.createElement(options.listType);
    wrapper = wrapper.appendChild(list);

    if (count) {
      const li = document.createElement("li");
      wrapper = wrapper.appendChild(li);
    }
  }

  return wrapper;
}

function jumpBack(currentWrapper: HTMLElement, offset: number): HTMLElement {
  while (offset--) {
    if (currentWrapper.parentElement) {
      currentWrapper = currentWrapper.parentElement;
    }
  }

  return currentWrapper;
}

function setAttrs(overwrite: boolean, prefix: string) {
  return function (src: HTMLElement, target: HTMLAnchorElement, index: number) {
    const content = src.textContent;
    const pre = prefix + "-" + index;
    target.textContent = content;

    let id = overwrite ? pre : src.id || pre;

    id = encodeURIComponent(id);

    src.id = id;
    target.href = "#" + id;
  };
}

function buildTOC(options: TocOptions) {
  const selector = options.selector;
  const scope = options.scope;
  const listType = options.listType;

  const ret = document.createElement(listType);

  if (options.includeClasses) {
    ret.setAttribute("class", "nav flex-column");
  }

  let wrapper = ret;

  let lastLi: HTMLElement | null = null;

  const _setAttrs = setAttrs(options.overwrite, options.prefix);

  getHeaders(selector, scope).reduce(function (prev, cur, index) {
    const currentLevel = getLevel(cur.tagName);
    const offset = currentLevel - prev;

    if (offset > 0) {
      if (lastLi) {
        wrapper = createList(lastLi, offset, options);
      } else {
        throw new Error();
      }
    }

    if (offset < 0) {
      wrapper = jumpBack(wrapper, -offset * 2);
    }

    wrapper = wrapper || ret;

    const li = document.createElement("li");

    if (options.includeClasses) {
      li.setAttribute("class", "nav-item");
    }
    const a = document.createElement("a");

    if (options.includeClasses) {
      a.setAttribute("class", "nav-link");
    }

    _setAttrs(cur, a, index);

    wrapper.appendChild(li).appendChild(a);

    lastLi = li;

    return currentLevel;
  }, getLevel(selector));

  return ret;
}

function initToc(options: TocOptions) {
  const selector = options.selector;

  if (typeof selector !== "string") {
    throw new TypeError("selector must be a string");
  }

  if (!selector.match(/^(?:h[1-6],?\s*)+$/g)) {
    throw new TypeError("selector must contains only h1-6");
  }

  const currentHash = location.hash;

  if (currentHash) {
    setTimeout(function () {
      const anchor = document.getElementById(currentHash.slice(1));
      if (anchor) anchor.scrollIntoView();
    }, 0);
  }

  return buildTOC(options);
}

export function InitPageToc() {
  const elem = document.getElementById("page-toc-nav");

  const others = document.querySelectorAll(".page-toc-nav");

  const all: HTMLElement[] = Array.prototype.slice.call(others);

  if (elem && all.indexOf(elem) === -1) {
    all.push(elem);
  }

  if (all.length === 0) {
    return;
  }

  for (const elem of all) {
    const tocOptions = new TocOptions();

    if (elem.dataset.tocSelector) {
      tocOptions.selector = elem.dataset.tocSelector;
    }

    if (elem.dataset.tocScope) {
      tocOptions.scope = elem.dataset.tocScope;
    }

    if (elem.dataset.tocListType) {
      tocOptions.listType = elem.dataset.tocListType;
    }

    tocOptions.overwrite = parseBooleanData(elem.dataset.tocOverwrite);

    if (elem.dataset.tocPrefix) {
      tocOptions.prefix = elem.dataset.tocPrefix;
    }

    tocOptions.includeClasses = parseBooleanData(elem.dataset.tocIncludeClasses);

    const toc = initToc(tocOptions);

    elem.appendChild(toc);
  }
}

const parseBooleanData = (value: string | null | undefined): boolean => {
  if (value === "true") {
    return true;
  }

  if (value === "false") {
    return false;
  }

  if (value === null || value === undefined) {
    return false;
  }

  return true;
};

InitPageToc();
